import React from 'react';
import styled from 'styled-components';
import { border, compose, margin } from 'styled-system';

import { getSizes, ImageProps, NOT_FORWARDED_PROPS, SrcsetArray } from './Image.common';

type SrcsetReturnType = {
  src: string;
  srcSet?: string;
  sizes?: string;
};

export type Props = ImageProps;

const SRCSET_DEFAULT_WIDTHS = ['767w', '991w', '1440w'];

const getSrcset = (sources: SrcsetArray, srcsetWidths?: SrcsetArray): string => {
  const srcsetArray: string[] = [];
  for (let i = 0; i < sources.length; i += 1) {
    if (sources[i]) {
      srcsetArray.push(
        `${sources[i]} ${
          srcsetWidths && srcsetWidths[i] ? `${srcsetWidths[i]}w` : SRCSET_DEFAULT_WIDTHS[i]
        }`
      );
    }
  }
  return srcsetArray.join(', ');
};

const getLargestSrc = (sources: SrcsetArray): string => {
  const flattenedSources = sources.filter(Boolean) as string[];
  return flattenedSources[flattenedSources.length - 1] || '';
};

const getSrcOrSrcset = (
  src: string | SrcsetArray,
  sizes?: string | string[],
  srcsetWidths?: SrcsetArray
): SrcsetReturnType => {
  if (Array.isArray(src)) {
    return {
      src: getLargestSrc(src),
      srcSet: getSrcset(src, srcsetWidths),
      sizes: sizes ? getSizes(sizes) : '100vw',
    };
  }
  return { src };
};

const Image = styled(
  ({
    resizeMethod = 'cover',
    description,
    src,
    onLoad,
    webProps = {},
    testID,

    // pull out rxn-specific props and ignore them
    rxnProps, // eslint-disable-line @typescript-eslint/no-unused-vars

    ...other
  }: Props) => {
    const {
      sizes,
      srcsetWidths,
      src: webSrc,
      description: webDescription,
      eager = false,
      ...otherWebProps
    } = webProps;

    // prefer the webProps for common props
    const source = webSrc || src;
    const alt = webDescription || description;

    // return null if no src is provided
    if (!source) {
      return null;
    }

    return (
      <img
        {...other}
        {...otherWebProps}
        {...getSrcOrSrcset(source, sizes, srcsetWidths)}
        alt={alt}
        style={{
          objectFit: resizeMethod !== 'center' ? resizeMethod : 'none',
          objectPosition: resizeMethod === 'center' ? resizeMethod : 'auto',
        }}
        loading={eager ? 'eager' : 'lazy'}
        onLoad={(event): void => {
          if (onLoad)
            onLoad({
              width: event.currentTarget.width,
              height: event.currentTarget.height,
              uri: event.currentTarget.src,
            });
        }}
        data-testid={testID}
      />
    );
  }
).withConfig({
  shouldForwardProp: (prop) => !NOT_FORWARDED_PROPS.includes(prop),
})<Props>(compose(margin, border));

/**
 * Image component with support for responsive images via srcset.
 * Includes lazy loading by default and accessibility features.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/3836051463/Kibble+Core+Image
 */
export default Image;
